import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Simplify } from 'type-fest';

import { getDeviceAlertsConfiguration } from '@/api/customSensorAlerts';
import { DeviceAlert, SensorAlertTypes } from '@/api/customSensorAlerts/types';
import {
    Detection,
    DetectionClassification,
    PerSensor,
    SoundPressureReading,
    useSoundPressureReadings,
} from '@/api/historicalData/index';
import { SoundPressureLevelUnits, Units } from '@/common/convert';
import { SpotterIdOrIds } from '@/common/react-query';
import { QuantityType } from '@/components/data/Datum';
import { DateRange } from '@/store';
import {
    AlertComparisonOperators,
    applicableDeviceAlerts,
    composeMostSevereBreachedAlerts,
    convertAlertThreshold,
    isAlertThresholdBreached,
} from '@/utils';

export const SPL_BREACH_CLASSIFICATION: DetectionClassification = 'splBreach';
export const SPL_BROADBAND_ALERT_TYPE: SensorAlertTypes = 'smartmooring_threshold_broadband_spl';
/** The fixed unit `bm_borealis_spl_broadband_reading` values are produced in. */
export const SPL_BROADBAND_READING_UNITS: SoundPressureLevelUnits = 'dB re µPa²';

type ConvertedAlertThreshold = {
    alert: DeviceAlert;
    convertedValue: number;
    convertedUnits: Units;
    unitType: QuantityType;
};

const applicableConvertedAlerts = (alerts: DeviceAlert[], sensorPosition: number) => {
    return applicableDeviceAlerts(alerts, SPL_BROADBAND_ALERT_TYPE, sensorPosition).map((alert) => ({
        alert,
        ...convertAlertThreshold(alert, SPL_BROADBAND_READING_UNITS),
        convertedUnits: SPL_BROADBAND_READING_UNITS,
    }));
};

const numericSensorPosition = (sensorPositionKey: string): number => {
    return Number(sensorPositionKey.replace(/^sensor-/, ''));
};

const createBreachDetections = (
    readings: SoundPressureReading[],
    convertedAlerts: ConvertedAlertThreshold[],
    sensorPosition: number
): Detection[] => {
    const alerts = convertedAlerts.map(({ alert, convertedValue, convertedUnits, unitType }) => ({
        id: alert.id,
        type: alert.trigger_details.trigger_type as SensorAlertTypes,
        logicalOperator: alert.trigger_details.operator as AlertComparisonOperators,
        convertedAlertValue: convertedValue,
        convertedAlertUnits: convertedUnits,
        unitType: unitType,
        displayName: alert.trigger_details.displayName.toLowerCase(),
    }));

    const detections: (Detection & { endTs: number })[] = [];
    readings.forEach((reading) => {
        if (isNaN(reading.broadbandSpl)) {
            // placeholder used to pad out spectrograms
            return;
        }
        const breachedAlerts = composeMostSevereBreachedAlerts(
            alerts.filter(({ logicalOperator, convertedAlertValue }) =>
                isAlertThresholdBreached(reading.broadbandSpl, logicalOperator, convertedAlertValue)
            )
        );
        const lastDetection = detections.length ? detections[detections.length - 1] : null;
        const endDate = new Date(reading.timestamp);
        const startTs = endDate.getTime() - 1000;
        if (breachedAlerts.length) {
            const alert = breachedAlerts[0];
            if (
                lastDetection &&
                lastDetection.is_detection &&
                lastDetection.endTs >= startTs - 500 &&
                lastDetection.breachedSensorAlert === alert
            ) {
                // if our last "detection" was based on the same alert threshold getting breached, and there was less than half a second in between,
                // call it the same detection to avoid inflating our charted dataset.
                lastDetection.end_time = endDate.toISOString();
                lastDetection.endTs = endDate.getTime();
            } else {
                detections.push({
                    classification: SPL_BREACH_CLASSIFICATION,
                    time_resolution: 'high',
                    start_time: new Date(startTs).toISOString(),
                    end_time: endDate.toISOString(),
                    endTs: endDate.getTime(),
                    is_detection: true,
                    breachedSensorAlert: alert,
                    // Detection requires these fields, but DetectionTimelineChart's renderer never reads
                    // geolocation or node id off any Detection - only start/end time, classification, and is_detection.
                    latitude: 0,
                    longitude: 0,
                    density: 100,
                    density_threshold: 0,
                    bristlemouth_node_id: '',
                    sensor_position: sensorPosition,
                });
            }
        } else {
            if (lastDetection && !lastDetection.is_detection && lastDetection.endTs >= startTs - 500) {
                // if our last "detection" was also not detected, and there was less than half a second in between, call it the same non-detection
                lastDetection.end_time = endDate.toISOString();
                lastDetection.endTs = endDate.getTime();
            } else {
                detections.push({
                    classification: SPL_BREACH_CLASSIFICATION,
                    time_resolution: 'high',
                    start_time: new Date(startTs).toISOString(),
                    end_time: endDate.toISOString(),
                    endTs: endDate.getTime(),
                    is_detection: false,
                    // Detection requires these fields, but DetectionTimelineChart's renderer never reads
                    // geolocation or node id off any Detection - only start/end time, classification, and is_detection.
                    latitude: 0,
                    longitude: 0,
                    density: 0,
                    density_threshold: 0,
                    bristlemouth_node_id: '',
                    sensor_position: sensorPosition,
                });
            }
        }
    });

    return detections;
};

const detectSplBreaches = (
    readingsBySensorPosition: PerSensor<SoundPressureReading[]>,
    alerts: DeviceAlert[]
): Detection[] => {
    return Object.entries(readingsBySensorPosition).flatMap(([sensorPositionKey, readings]) => {
        const sensorPosition = numericSensorPosition(sensorPositionKey);
        const convertedAlerts = applicableConvertedAlerts(alerts, sensorPosition);
        if (!convertedAlerts.length) {
            return [];
        }
        return createBreachDetections(readings, convertedAlerts, sensorPosition);
    });
};

export type UseSplBreachDetectionsOptions = {
    dateRange?: DateRange | null;
    config?: { enabled?: boolean };
} & SpotterIdOrIds;

type UseSplBreachDetectionsResult<TData> = { data: TData | undefined; isPending: boolean; isFetching: boolean };

export function useSplBreachDetections(
    options: Simplify<Omit<UseSplBreachDetectionsOptions, 'spotterIds'>>
): UseSplBreachDetectionsResult<Detection[]>;

export function useSplBreachDetections(
    options: Simplify<Omit<UseSplBreachDetectionsOptions, 'spotterId'>>
): UseSplBreachDetectionsResult<(Detection[] | undefined)[]>;

export function useSplBreachDetections({
    spotterId,
    spotterIds,
    dateRange,
    config: { enabled = true } = {},
}: UseSplBreachDetectionsOptions): UseSplBreachDetectionsResult<Detection[] | (Detection[] | undefined)[]> {
    const requestedSpotterIds = useMemo(() => (spotterId ? [spotterId] : (spotterIds ?? [])), [spotterId, spotterIds]);

    const queriesEnabled = enabled && Boolean(requestedSpotterIds.length);

    const {
        data: readingsBySpotterId,
        isPending: isReadingsPending,
        isFetching: isReadingsFetching,
    } = useSoundPressureReadings({
        spotterIds: requestedSpotterIds,
        dateRange,
        config: { enabled: queriesEnabled },
    });

    const { alertsBySpotterId, isAlertsPending, isAlertsFetching } = useQueries({
        queries: (requestedSpotterIds ?? []).map((alertSpotterId) => ({
            queryKey: ['device-alerts-configuration', { spotterId: alertSpotterId }],
            queryFn: getDeviceAlertsConfiguration,
            enabled: queriesEnabled,
            staleTime: 1000 * 60 * 5, // 5 minutes
        })),
        combine: (results) => ({
            alertsBySpotterId: results.map((result) => result.data?.data),
            isAlertsPending: results.some((result) => result.isPending),
            isAlertsFetching: results.some((result) => result.isFetching),
        }),
    });

    const data = useMemo(() => {
        const detectionsBySpotterId: Record<string, Detection[]> = {};

        requestedSpotterIds?.forEach((id, index) => {
            const readings = readingsBySpotterId?.[index];
            const alerts = alertsBySpotterId[index];
            if (readings && alerts) {
                detectionsBySpotterId[id] = detectSplBreaches(readings, alerts);
            }
        });

        if (spotterId) {
            return detectionsBySpotterId[spotterId];
        }
        return requestedSpotterIds.map((id) => detectionsBySpotterId[id]);
    }, [spotterId, requestedSpotterIds, readingsBySpotterId, alertsBySpotterId]);

    return {
        data,
        isPending: isReadingsPending || isAlertsPending,
        isFetching: isReadingsFetching || isAlertsFetching,
    };
}

export const exportForTests = {
    detectSplBreaches,
};
